import { css } from '@emotion/react';
import { BREAKPOINTS, semanticTokens, tokens } from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import Skeleton from 'react-loading-skeleton';

const styles = {
  button: (isActive: boolean) => css`
    font-family: unset;
    border: none;
    font-weight: 700;

    padding: ${tokens.dimension['space-3']};

    color: ${!isActive && semanticTokens.color.text.secondary};
    background-color: ${semanticTokens.color.background.secondary};
    :hover {
      background-color: ${isActive ? semanticTokens.color.fill['transparent-hover'] : 'transparent'};
    }

    cursor: ${isActive ? 'pointer' : 'default'};
    border-radius: ${tokens.cornerRadius.medium};
  `,
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${tokens.dimension['space-3']};
  `,
  name: css`
    font-weight: 400;
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};
    color: ${semanticTokens.color.text.primary};
    margin-bottom: ${tokens.dimension['space-3']};
    margin: 0;
  `,
  price: (isDiscounted: boolean) => css`
    color: ${isDiscounted
      ? semanticTokens.color.surface['critical-strong']
      : semanticTokens.color.surface['primary-brand']};
    font-size: ${tokens.fontSize.paragraph['paragraph-3']};
    display: flex;
    flex-direction: column;
    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      font-size: ${tokens.fontSize.paragraph['paragraph-4']};
    }
    p {
      margin: 0;
    }
  `,
  oldPrice: css`
    color: ${semanticTokens.color.text['secondary']};
    font-size: ${tokens.fontSize.paragraph['paragraph-1']};
    display: flex;
    font-weight: 100; // Not sure what weight this should be
    flex-direction: column;
    text-decoration: line-through;
    margin: 0;
    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      font-size: ${tokens.fontSize.paragraph['paragraph-3']};
    }
  `,
  availableSeats: css`
    font-size: ${tokens.fontSize.paragraph['paragraph-1']};
    font-weight: 400;
    color: ${semanticTokens.color.text.critical};
    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      position: absolute;
    }
    margin: 0;
  `,
  seatContainer: (isVisible: boolean) => css`
    display: flex;
    justify-content: center;
    visibility: ${isVisible ? 'visible' : 'hidden'};
  `,
  fullyBooked: css`
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};
    color: ${semanticTokens.color.text.secondary};
    margin: 0;
  `,
};

type Props = {
  name: string;
  availableSeats: number;
  formattedPrice?: string;
  rawPrice?: string;
  isSelected: boolean;
  handleClick: () => void;
  testId?: string;
  originalTotalPrice?: string;
};

export function FlightLowestFare({
  name,
  formattedPrice,
  rawPrice,
  availableSeats,
  isSelected,
  handleClick,
  testId,
  originalTotalPrice,
}: Props) {
  const isActive = Boolean(formattedPrice && rawPrice);
  const shouldDisplayAvailableSeats = availableSeats < 9 && isActive;

  let ariaLabel: string | undefined;
  if (isActive) {
    ariaLabel = t('a11y.selectFlightFare', { name, price: rawPrice });
    if (shouldDisplayAvailableSeats) {
      ariaLabel += ` ${t('seats.left', { seats: availableSeats })}`;
    }
  }

  const hasDiscountApplied = Boolean(originalTotalPrice);
  return (
    <button
      data-testid={testId}
      aria-label={ariaLabel}
      aria-pressed={isSelected}
      disabled={!isActive}
      aria-disabled={!isActive}
      css={styles.button(isActive)}
      onClick={e => {
        e.stopPropagation();
        if (isActive) handleClick();
      }}
    >
      <div css={styles.container}>
        <p css={styles.name}>{name}</p>

        {isActive ? (
          <>
            <div css={styles.price(hasDiscountApplied)}>
              <p>{formattedPrice}</p>
            </div>
            {hasDiscountApplied && <p css={styles.oldPrice}>{originalTotalPrice}</p>}
          </>
        ) : (
          <p css={styles.fullyBooked}>{t('common:fullyBooked', { defaultValue: 'Fully booked' })}</p>
        )}
      </div>

      {shouldDisplayAvailableSeats && (
        <div css={styles.seatContainer(true)}>
          <p css={styles.availableSeats}>{t('seats.left', { seats: availableSeats })}</p>
        </div>
      )}
    </button>
  );
}

export function FlightLowestFareSkeleton() {
  return (
    <button css={styles.button(true)} onClick={() => {}}>
      <div css={styles.container}>
        <p css={styles.name}>
          <Skeleton width={24} height={12} />
        </p>

        <div css={styles.price(false)}>
          <Skeleton width={60} height={12} />
        </div>
      </div>
    </button>
  );
}
